import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useOutletContext } from 'react-router-dom';
import DashboardHeader from '../../components/dashboard/DashboardHeader';
import DashboardStats from '../../components/dashboard/DashboardStats';
import TodaySchedule from '../../components/dashboard/TodaySchedule';
import AttentionItems, { buildAttentionItems } from '../../components/dashboard/AttentionItems';
import SportsOverview from '../../components/dashboard/SportsOverview';
import QuickActions from '../../components/dashboard/QuickActions';
import RecentActivity from '../../components/dashboard/RecentActivity';
import DashboardSkeleton from '../../components/dashboard/DashboardSkeleton';
import FinancialTrendChart from '../../components/dashboard/FinancialTrendChart';
import SubscriptionAlerts from '../../components/dashboard/SubscriptionAlerts';
import { canViewFinance } from '../../config/organization';
import { defaultPeriodRange } from '../../lib/dashboardPeriods';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OrganizationDashboard() {
  const { user, club, trialStatus } = useOutletContext() || {};
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [matches, setMatches] = useState([]);
  const [financial, setFinancial] = useState(null);
  const [trend, setTrend] = useState([]);
  const [subAlerts, setSubAlerts] = useState(null);
  const [activities, setActivities] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(defaultPeriodRange());
  const isFirstLoad = useRef(true);

  const financeAllowed = canViewFinance(user);

  const fetchAll = useCallback(async (periodValue) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    if (isFirstLoad.current) setLoading(true); else setRefreshing(true);
    setError(null);

    const isAll = !periodValue || periodValue.preset === 'all';
    const rangeQuery = isAll ? '' : `?start_date=${periodValue.startDate}&end_date=${periodValue.endDate}`;
    const financeQuery = isAll ? '?period=all' : rangeQuery;

    const requests = [
      axios.get(`${API}/statistics/dashboard${rangeQuery}`, auth),
      axios.get(`${API}/matches`, auth),
      axios.get(`${API}/activity-log`, auth),
      financeAllowed ? axios.get(`${API}/financial/dashboard${financeQuery}`, auth) : Promise.reject(new Error('skipped')),
      financeAllowed ? axios.get(`${API}/financial/monthly-trend?months=6`, auth) : Promise.reject(new Error('skipped')),
      financeAllowed ? axios.get(`${API}/players/subscription-alerts`, auth) : Promise.reject(new Error('skipped')),
    ];

    const [statsRes, matchesRes, activityRes, financeRes, trendRes, subAlertsRes] = await Promise.allSettled(requests);

    if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
    if (matchesRes.status === 'fulfilled' && Array.isArray(matchesRes.value.data)) setMatches(matchesRes.value.data);
    if (activityRes.status === 'fulfilled' && Array.isArray(activityRes.value.data)) setActivities(activityRes.value.data);
    if (financeRes.status === 'fulfilled') setFinancial(financeRes.value.data);
    if (trendRes.status === 'fulfilled' && Array.isArray(trendRes.value.data)) setTrend(trendRes.value.data);
    if (subAlertsRes.status === 'fulfilled') setSubAlerts(subAlertsRes.value.data);

    if (statsRes.status === 'rejected' && matchesRes.status === 'rejected') {
      setError('تعذر تحميل بيانات لوحة التحكم. تحقق من الاتصال وحاول مجدداً.');
    }
    if (isFirstLoad.current) {
      setLoading(false);
      isFirstLoad.current = false;
    } else {
      setRefreshing(false);
    }
  }, [financeAllowed]);

  useEffect(() => {
    fetchAll(selectedPeriod);
  }, [fetchAll, selectedPeriod]);

  const now = new Date();
  const upcomingMatches = matches
    .filter((m) => {
      const d = new Date(m.match_date);
      return !isNaN(d.getTime()) && d >= now;
    })
    .sort((a, b) => new Date(a.match_date) - new Date(b.match_date))
    .slice(0, 4);

  const nextMatch = upcomingMatches[0] || null;

  const recentResults = matches
    .filter((m) => m.home_score !== null && m.home_score !== undefined && m.away_score !== null && m.away_score !== undefined)
    .sort((a, b) => new Date(b.match_date) - new Date(a.match_date))
    .slice(0, 4);

  const attentionItems = buildAttentionItems({ matches });

  return (loading || !club) ? (
    <DashboardSkeleton />
  ) : error ? (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-slate-200 bg-white py-12 text-center" data-testid="dashboard-error">
      <p className="text-sm text-slate-600">{error}</p>
      <button onClick={() => fetchAll(selectedPeriod)} className="rounded-lg bg-[#16233b] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1e2f4e]" data-testid="dashboard-retry-btn">
        إعادة المحاولة
      </button>
    </div>
  ) : (
    <div className="mx-auto max-w-7xl space-y-4" data-testid="organization-dashboard">
      <DashboardHeader
        user={user}
        club={club}
        trialStatus={trialStatus}
        periodRange={selectedPeriod}
        onPeriodChange={setSelectedPeriod}
      />
      <div className={refreshing ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
        <DashboardStats stats={stats} financial={financial} showFinance={financeAllowed} user={user} />
      </div>
      <QuickActions user={user} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <TodaySchedule upcomingMatches={upcomingMatches} />
          {financeAllowed && <FinancialTrendChart trend={trend} />}
          <AttentionItems items={attentionItems} />
        </div>
        <div className="space-y-4">
          <SportsOverview recentResults={recentResults} nextMatch={nextMatch} />
          {financeAllowed && <SubscriptionAlerts alerts={subAlerts} />}
          <RecentActivity activities={activities} />
        </div>
      </div>
    </div>
  );
}
